import { useEffect } from 'react'

import * as d3 from 'd3'
import * as geometric from 'geometric'

import { DeviceType } from '@/graphql/generated/schemas'

import {
  createCameraViewPath,
  getCheckPointIcon,
  getEditCheckPointIcon,
  getEditCheckPointText,
  getEditCheckPointWidth,
  getRemoveIconOpacity,
  getTailgateConnectionElementId,
  handleCheckPointDeviceDrag,
} from '../utils/chart'
import { angleDrag, angleWidthDrag, distanceDrag } from '../utils/drag'

const IMAGE_PADDING = 80

export const DEFAULT_ICON_SIZE = 24

export const useFloorChart = ({
  floorPlanIconSize,
  devices,
  checkpoints,
  connections,
  interactionsEnabled,
  onDeviceUpdated,
  onDeviceReleased,
  onDeviceClicked,
  onDeviceHover,
  onDeviceHoverOut,
  onRemoveCheckpoint,
  onMoveCheckpoint,
  onConnectionButtonClicked,
  highlightedDeviceIds,
}) => {
  const onZoom = ({ transform }) => {
    // Can get zoom level with `transform.k`
    d3.select('.map-group').attr('transform', transform)
  }

  const getZoom = (minScale) => {
    return d3.zoom().scaleExtent([minScale, 10]).on('zoom', onZoom)
  }

  const handleDeviceDrag = d3
    .drag()
    .on('drag', (event, d) => {
      if (interactionsEnabled) {
        d.x = event.x
        d.y = event.y
        d.dragged = true
        onDeviceUpdated(d)
      }
    })
    .on('end', (event, d) => {
      if (interactionsEnabled) {
        d.x = event.x
        d.y = event.y
        d.dragged = false
        onDeviceReleased(d)
      }
    })

  const handleRotationDrag = angleDrag((d, angle) => {
    if (!interactionsEnabled) return
    d.rotation = angle
    onDeviceUpdated(d)
  })

  const handleDistanceDrag = distanceDrag((d, length) => {
    if (!interactionsEnabled) return
    d.distance = length
    onDeviceUpdated(d)
  })

  const handleAngleWidthDrag = angleWidthDrag((d, angle) => {
    if (!interactionsEnabled) return
    d.angle = angle
    onDeviceUpdated(d)
  })

  const calculateRotationHandleTransform = (d) => {
    const position = geometric.pointTranslate(
      [0, 0],
      d.rotation - 90,
      d.distance + 100 // 100 is spacing between handle and the device
    )
    return `translate(${position}) rotate(${d.rotation})`
  }

  const calculateDistanceHandleTransform = (d) => {
    const position = geometric.pointTranslate(
      [0, 0],
      d.rotation - 90,
      d.distance
    )
    return `translate(${position}) rotate(${d.rotation + 90})`
  }

  const calculateAngleWidthHandleTransform = (d) => {
    const position = geometric.pointTranslate(
      [0, 0],
      d.rotation - 90 + d.angle * 0.5,
      d.distance
    )
    return `translate(${position}) rotate(${d.rotation + d.angle * 0.5})`
  }

  const cameraHasIncidents = (d) => {
    return d.incidents && d.incidents > 0
  }

  /**
   * Calculate string used to annotate device icon on a floor plan view.
   *
   * @param {object} d
   *  Device object.
   * @returns String representation of number of incidents.
   */
  const calculateDisplayedNumberOfIncidents = (d) =>
    d.incidents >= 1000 ? '1k+' : `${d.incidents}`

  const isIncidentElementVisible = (d) => {
    return cameraHasIncidents(d) ? 'visible' : 'hidden'
  }

  const isHandleVisible = (d) => {
    return d.isEditMode ? 'visible' : 'hidden'
  }

  const getConnectionIcon = (d) => {
    if (d.deviceType === DeviceType.Alarm) {
      return 'alarmAssociation.png'
    }
    return d.isConnected && d.isTailgateGridConfigured
      ? 'TailgateGreen.png'
      : 'TailgateWhite.png'
  }

  const connectionLine = (lineCoords) => {
    const line = d3.line()
    return line(lineCoords)
  }

  // region Camera
  // For some reason this param should be doubled to match other icon sizes
  const cameraEnter = (enter, size) => {
    // Calculate derived sizes
    const halfSize = size / 2
    const circleRadius = halfSize
    const iconSize = size * (24 / 44) // Scale icon proportionally to original ratio
    const halfIconSize = iconSize / 2
    const incidentCircleRadius = size * (12 / 44)
    const incidentCircleOffset = size * (20 / 44)
    const incidentTextOffset = size * (20 / 44)
    const incidentTextYOffset = size * (25 / 44)
    const incidentIconTranslate = [size * (-28 / 44), size * (11 / 44)]
    const incidentIconScale = size * (0.7 / 44)

    const g = enter
      .append('g')
      .attr('class', 'camera')
      .classed('editOn', (d) => d.isEditMode)
      .classed('hasIncidents', cameraHasIncidents)
      .attr('id', (d) => {
        return `device-${d.id}`
      })
      .attr('transform', (d) => `translate(${d.x}, ${d.y})`)
      .on('click', (event, d) => {
        onDeviceClicked(d)
        event.stopPropagation()
      })
      .on('mouseover', (event, d) => {
        onDeviceHover(d, d.id, 'camera')
      })
      .on('mouseout', (event, d) => {
        onDeviceHoverOut(d, d.id, 'camera')
      })
      .call(handleDeviceDrag)

    g.append('circle')
      .attr('class', 'cameraCircle')
      .attr('transform', (d) => `rotate(${d.rotation})`)
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', circleRadius)

    g.append('image')
      .attr('class', 'cameraIconSvg')
      // -90 only because the image is pointing East
      .attr('transform', (d) => `rotate(${d.rotation - 90})`)
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('href', 'video.png')
      .attr('x', -halfIconSize)
      .attr('y', -halfIconSize)

    g.append('circle')
      .attr('class', 'incidentCountCircle')
      .attr('cx', incidentCircleOffset)
      .attr('cy', incidentCircleOffset)
      .attr('r', incidentCircleRadius)
      .attr('visibility', isIncidentElementVisible)

    g.append('circle')
      .attr('class', 'incidentIconCircle')
      .attr('cx', -incidentCircleOffset)
      .attr('cy', incidentCircleOffset)
      .attr('r', incidentCircleRadius)
      .attr('visibility', isIncidentElementVisible)

    g.append('text')
      .attr('class', 'deviceIncidentLabel')
      .attr('dx', incidentTextOffset)
      .attr('dy', incidentTextYOffset)
      .attr('text-anchor', 'middle')
      .attr('visibility', isIncidentElementVisible)
      .text(calculateDisplayedNumberOfIncidents)

    g.append('path')
      .attr('class', 'incidentIcon')
      .attr('visibility', isIncidentElementVisible)
      .attr(
        'transform',
        `translate(${incidentIconTranslate}) scale(${incidentIconScale})`
      )
      .attr(
        'd',
        'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
      )
  }

  const cameraUpdate = (update, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 42

    update
      .select('.cameraIconSvg')
      // -90 only because the image is pointing East
      .attr('transform', (d) => `rotate(${d.rotation - 90})`)
      .attr('width', `${iconSize / 2}px`)
      .attr('height', `${iconSize / 2}px`)
      .attr('x', -iconSize / 4)
      .attr('y', -iconSize / 4)

    update.select('.cameraCircle').attr('r', iconSize / 2) // Update circle radius

    // Update incident indicators if present
    update
      .select('.incidentCountCircle')
      .attr('cx', 20 * scaleFactor)
      .attr('cy', 20 * scaleFactor)
      .attr('r', 12 * scaleFactor)

    update
      .select('.incidentIconCircle')
      .attr('cx', -20 * scaleFactor)
      .attr('cy', 20 * scaleFactor)
      .attr('r', 12 * scaleFactor)

    update
      .select('.deviceIncidentLabel')
      .attr('dx', 20 * scaleFactor)
      .attr('dy', 25 * scaleFactor)
      .attr('font-size', `${12 * scaleFactor}px`)

    update
      .select('.incidentIcon')
      .attr(
        'transform',
        `translate(${-28 * scaleFactor},${11 * scaleFactor}) scale(${
          0.7 * scaleFactor
        })`
      )

    update.attr('id', (d) => {
      return `device-${d.id}`
    })
    update.attr(
      'transform',
      (d) => `translate(${d.projectedX}, ${d.projectedY})`
    )

    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)
    update.classed('isHighlighted', (d) =>
      highlightedDeviceIds?.find((id) => d.id === id)
    )
  }

  // For when you're able to adjust the field of view for a camera
  const cameraControlEnter = (enter, size) => {
    // Calculate half size for positioning elements
    const halfSize = size / 2

    const g = enter
      .append('g')
      .attr('class', 'cameraControl')
      .classed('editOn', (d) => d.isEditMode)
      .attr('id', (d) => `${d.id}Control`)
      .attr('transform', (d) => `translate(${d.projectedX}, ${d.projectedY})`)
      .on('click', (event, d) => {
        onDeviceClicked(d)
        event.stopPropagation()
      })
      .call(handleDeviceDrag)

    g.append('path')
      .datum((d) => createCameraViewPath(d.distance, d.rotation, d.angle))
      .attr('d', d3.arc())
      .attr('class', 'cameraView')

    g.append('image')
      .attr('class', 'floorDeviceHandle handleRotation')
      .attr('visibility', isHandleVisible)
      .attr('width', `${size}px`)
      .attr('height', `${size}px`)
      .attr('href', 'ArrowHandleCorner.png')
      .attr('x', 0 - halfSize)
      .attr('y', 0 - halfSize)
      .attr('transform', calculateRotationHandleTransform)
      .call(handleRotationDrag)

    g.append('image')
      .attr('class', 'floorDeviceHandle handleDistance')
      .attr('visibility', isHandleVisible)
      .attr('width', `${size}px`)
      .attr('height', `${size}px`)
      .attr('href', 'ArrowHandle.png')
      .attr('x', 0 - halfSize)
      .attr('y', 0 - halfSize)
      .attr('transform', calculateDistanceHandleTransform)
      .call(handleDistanceDrag)

    g.append('image')
      .attr('class', 'floorDeviceHandle handleAngleWidth')
      .attr('visibility', isHandleVisible)
      .attr('width', `${size}px`)
      .attr('height', `${size}px`)
      .attr('href', 'ArrowHandle.png')
      .attr('x', 0 - halfSize)
      .attr('y', 0 - halfSize)
      .attr('transform', calculateAngleWidthHandleTransform)
      .call(handleAngleWidthDrag)
  }

  const cameraControlUpdate = (update, iconSize) => {
    // No need to scale path as it's generated by d3.arc()
    update
      .select('path')
      .datum((d) => createCameraViewPath(d.distance, d.rotation, d.angle))
      .attr('d', d3.arc())

    // Scale handler images
    update
      .select('.handleRotation')
      .attr('visibility', isHandleVisible)
      .attr('transform', calculateRotationHandleTransform)
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    update
      .select('.handleDistance')
      .attr('visibility', isHandleVisible)
      .attr('transform', calculateDistanceHandleTransform)
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    update
      .select('.handleAngleWidth')
      .attr('visibility', isHandleVisible)
      .attr('transform', calculateAngleWidthHandleTransform)
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    update.attr('transform', (d) => `translate(${d.x}, ${d.y})`)

    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)
  }
  // endregion

  const doorEnter = (enter, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    // Base dimensions that will be scaled
    const baseCircleRadius = 22
    const baseIncidentCircleRadius = 12
    const baseIncidentCircleOffsetX = 20
    const baseIncidentCircleOffsetY = 20
    const baseTextOffsetX = 20
    const baseTextOffsetY = 25
    const baseWarningIconOffset = [-28, 11]
    const baseWarningIconScale = 0.7

    const g = enter
      .append('g')
      .attr('class', 'door')
      .classed('editOn', (d) => d.isEditMode)
      .classed('hasIncidents', cameraHasIncidents)
      .attr('id', (d) => `device-${d.id}`)
      .attr('transform', (d) => `translate(${d.projectedX}, ${d.projectedX})`)
      .on('click', (event, d) => {
        onDeviceClicked(d)
        event.stopPropagation()
      })
      .on('mouseover', (event, d) => {
        onDeviceHover(d, d.id, 'door')
      })
      .on('mouseout', (event, d) => {
        onDeviceHoverOut(d, d.id, 'door')
      })
      .call(handleDeviceDrag)

    g.append('circle')
      .attr('class', 'cameraCircle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', baseCircleRadius * scaleFactor)

    g.append('image')
      .attr('class', 'cameraIconSvg')
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('href', 'door.png')
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    g.append('circle')
      .attr('class', 'incidentCountCircle')
      .attr('cx', baseIncidentCircleOffsetX * scaleFactor)
      .attr('cy', baseIncidentCircleOffsetY * scaleFactor)
      .attr('r', baseIncidentCircleRadius * scaleFactor)
      .attr('visibility', isIncidentElementVisible)

    g.append('circle')
      .attr('class', 'incidentIconCircle')
      .attr('cx', -baseIncidentCircleOffsetX * scaleFactor)
      .attr('cy', baseIncidentCircleOffsetY * scaleFactor)
      .attr('r', baseIncidentCircleRadius * scaleFactor)
      .attr('visibility', isIncidentElementVisible)

    g.append('text')
      .attr('class', 'deviceIncidentLabel')
      .attr('dx', baseTextOffsetX * scaleFactor)
      .attr('dy', baseTextOffsetY * scaleFactor)
      .attr('text-anchor', 'middle')
      .attr('visibility', isIncidentElementVisible)
      .attr('font-size', `${12 * scaleFactor}px`) // Scale text size as well
      .text(calculateDisplayedNumberOfIncidents)

    g.append('path')
      .attr('class', 'incidentIcon')
      .attr('visibility', isIncidentElementVisible)
      .attr(
        'transform',
        `translate(${baseWarningIconOffset[0] * scaleFactor},${
          baseWarningIconOffset[1] * scaleFactor
        }) scale(${baseWarningIconScale * scaleFactor})`
      )
      .attr(
        'd',
        'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
      )
  }

  const doorUpdate = (update, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    update
      .select('.cameraIconSvg')
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    update.select('.cameraCircle').attr('r', 22 * scaleFactor)

    // Update incident indicators
    update
      .select('.incidentCountCircle')
      .attr('cx', 20 * scaleFactor)
      .attr('cy', 20 * scaleFactor)
      .attr('r', 12 * scaleFactor)

    update
      .select('.incidentIconCircle')
      .attr('cx', -20 * scaleFactor)
      .attr('cy', 20 * scaleFactor)
      .attr('r', 12 * scaleFactor)

    update
      .select('.deviceIncidentLabel')
      .attr('dx', 20 * scaleFactor)
      .attr('dy', 25 * scaleFactor)
      .attr('font-size', `${12 * scaleFactor}px`)

    update
      .select('.incidentIcon')
      .attr(
        'transform',
        `translate(${-28 * scaleFactor},${11 * scaleFactor}) scale(${
          0.7 * scaleFactor
        })`
      )

    update.attr(
      'transform',
      (d) => `translate(${d.projectedX}, ${d.projectedY})`
    )
    update.attr('id', (d) => {
      return `device-${d.id}`
    })

    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)
    update.classed('isHighlighted', (d) =>
      highlightedDeviceIds?.find((id) => d.id === id)
    )
  }

  /**
   * The enter & update methods in this file are a total mess. Basically we
   * seem to be creating each of these icons from scratch each time we need
   * them, but they were hardcoded to always be a set size.
   * I fed each of these to Claude and asked that they be made resizable. While
   * they work, it's SUPER messy.
   *
   * Tbh, I don't think it's worth spending any more time to try and clean
   * this up as this whole thing needs to be re-worked, and I've created
   * ENG-20090 to track this work
   */

  // Alarm
  const alarmEnter = (enter, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    // Base dimensions that will be scaled
    const baseCircleRadius = 22
    const baseIncidentCircleRadius = 12
    const baseIncidentCircleOffsetX = 20
    const baseIncidentCircleOffsetY = 20
    const baseTextOffsetX = 20
    const baseTextOffsetY = 25
    const baseWarningIconOffset = [-28, 11]
    const baseWarningIconScale = 0.7

    const g = enter
      .append('g')
      .attr('class', 'alarm')
      .classed('editOn', (d) => d.isEditMode)
      .classed('hasIncidents', cameraHasIncidents)
      .attr('id', (d) => `device-${d.id}`)
      .attr('transform', (d) => `translate(${d.projectedX}, ${d.projectedX})`)
      .on('click', (event, d) => {
        onDeviceClicked(d)
        event.stopPropagation()
      })
      .on('mouseover', (event, d) => {
        onDeviceHover(d, d.id, 'alarm')
      })
      .on('mouseout', (event, d) => {
        onDeviceHoverOut(d, d.id, 'alarm')
      })
      .call(handleDeviceDrag)

    g.append('circle')
      .attr('class', 'cameraCircle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', baseCircleRadius * scaleFactor)

    g.append('image')
      .attr('class', 'alarmIconSvg')
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('href', 'alarm.png')
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    g.append('circle')
      .attr('class', 'incidentIconCircle')
      .attr('cx', baseIncidentCircleOffsetX * scaleFactor)
      .attr('cy', baseIncidentCircleOffsetY * scaleFactor)
      .attr('r', baseIncidentCircleRadius * scaleFactor)
      .attr('visibility', isIncidentElementVisible)

    g.append('circle')
      .attr('class', 'incidentIconCircle')
      .attr('cx', -baseIncidentCircleOffsetX * scaleFactor)
      .attr('cy', baseIncidentCircleOffsetY * scaleFactor)
      .attr('r', baseIncidentCircleRadius * scaleFactor)
      .attr('visibility', isIncidentElementVisible)

    g.append('text')
      .attr('class', 'deviceIncidentLabel')
      .attr('dx', baseTextOffsetX * scaleFactor)
      .attr('dy', baseTextOffsetY * scaleFactor)
      .attr('text-anchor', 'middle')
      .attr('visibility', isIncidentElementVisible)
      .attr('font-size', `${12 * scaleFactor}px`) // Scale text size as well
      .text(calculateDisplayedNumberOfIncidents)

    g.append('path')
      .attr('class', 'incidentIcon')
      .attr('visibility', isIncidentElementVisible)
      .attr(
        'transform',
        `translate(${baseWarningIconOffset[0] * scaleFactor},${
          baseWarningIconOffset[1] * scaleFactor
        }) scale(${baseWarningIconScale * scaleFactor})`
      )
      .attr(
        'd',
        'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
      )
  }

  const alarmUpdate = (update, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    update
      .select('.alarmIconSvg')
      .attr('width', `${iconSize}px`)
      .attr('height', `${iconSize}px`)
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)

    update.select('.cameraCircle').attr('r', 22 * scaleFactor)

    // Update incident indicators if present
    update
      .select('.incidentIconCircle')
      .attr('cx', 20 * scaleFactor)
      .attr('cy', 20 * scaleFactor)
      .attr('r', 12 * scaleFactor)

    update
      .select('.incidentIconCircle:nth-child(4)')
      .attr('cx', -20 * scaleFactor)
      .attr('cy', 20 * scaleFactor)
      .attr('r', 12 * scaleFactor)

    update
      .select('.deviceIncidentLabel')
      .attr('dx', 20 * scaleFactor)
      .attr('dy', 25 * scaleFactor)
      .attr('font-size', `${12 * scaleFactor}px`)

    update
      .select('.incidentIcon')
      .attr(
        'transform',
        `translate(${-28 * scaleFactor},${11 * scaleFactor}) scale(${
          0.7 * scaleFactor
        })`
      )

    update.attr(
      'transform',
      (d) => `translate(${d.projectedX}, ${d.projectedY})`
    )
    update.attr('id', (d) => {
      return `device-${d.id}`
    })

    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)
    update.classed('isHighlighted', (d) =>
      highlightedDeviceIds?.find((id) => d.id === id)
    )
  }
  // endregion

  const connectionEnter = (enter, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    // Base dimensions that will be scaled
    const baseCircleRadius = 16
    const baseIconWidth = 24
    const baseIconHeight = 15
    const baseIconOffsetX = 12
    const baseIconOffsetY = 8

    const g = enter
      .append('g')
      .attr('class', 'connection')
      .classed('isConnected', (d) => d.isConnected)
      .classed('gridConfigured', (d) => d.isTailgateGridConfigured)
      .attr('id', (d) => `connection-${getTailgateConnectionElementId(d)}`)
      .on('click', (event, d) => {
        onConnectionButtonClicked(d)
      })
      .on('mouseover', (event, d) => {
        onDeviceHover(d, d.id, 'tailgate')
      })
      .on('mouseout', (event, d) => {
        onDeviceHoverOut(d, d.id, 'tailgate')
      })

    g.append('path')
      .attr('class', 'connectionLine')
      .attr('d', (d) => connectionLine(d.lineCoords))
    // Note: The line path itself doesn't need scaling as its coordinates
    // are determined by the connections between devices

    g.append('circle')
      .attr('class', 'connectionCircle')
      .attr('cx', (d) => d.midX)
      .attr('cy', (d) => d.midY)
      .attr('r', baseCircleRadius * scaleFactor)

    g.append('image')
      .attr('class', 'connectionImage')
      .attr('width', `${baseIconWidth * scaleFactor}px`)
      .attr('height', `${baseIconHeight * scaleFactor}px`)
      .attr('href', getConnectionIcon)
      .attr('x', (d) => d.midX - baseIconOffsetX * scaleFactor)
      .attr('y', (d) => d.midY - baseIconOffsetY * scaleFactor)
  }

  const connectionUpdate = (update, iconSize) => {
    // Calculate scale factor based on the provided icon size
    const scaleFactor = iconSize / 24

    // Base dimensions that will be scaled
    const baseIconOffsetX = 12
    const baseIconOffsetY = 8

    update
      .classed('isConnected', (d) => d.isConnected)
      .classed('gridConfigured', (d) => d.isTailgateGridConfigured)

    update.select('path').attr('d', (d) => connectionLine(d.lineCoords))

    update
      .select('image')
      .attr('x', (d) => d.midX - baseIconOffsetX * scaleFactor)
      .attr('y', (d) => d.midY - baseIconOffsetY * scaleFactor)
      .attr('href', getConnectionIcon)
      .attr('width', `${24 * scaleFactor}px`)
      .attr('height', `${15 * scaleFactor}px`)

    update
      .select('.connectionCircle')
      .attr('cx', (d) => d.midX)
      .attr('cy', (d) => d.midY)
      .attr('r', 16 * scaleFactor)
  }

  const checkpointEnter = (enter, size) => {
    // Calculate derived dimensions based on size
    const halfSize = size / 2
    const tooltipHeight = size * 0.5
    const tooltipYPosition = -size * 1.25
    const tooltipTextYPosition = tooltipYPosition + tooltipHeight / 2 + 4
    const handleSize = size * 0.57 // ~32px when size is 56
    const handleYPosition = -size * 0.75
    const leftHandleXPosition = -size * 0.96 // ~-54px when size is 56
    const rightHandleXPosition = size * 0.5 // ~28px when size is 56
    const paddingForText = size * 0.36 // ~20px when size is 56

    const g = enter
      .append('g')
      .attr('class', 'checkpoint')
      .classed('editOn', (c) => c.isEditMode)
      .attr('id', (c) => `checkpoint-${c.id}`)
      .attr('transform', (c) => `translate(${c.x}, ${c.x})`)
      .call(
        handleCheckPointDeviceDrag(
          (d) => onDeviceUpdated(d),
          (d) => onDeviceReleased(d)
        )
      )

    g.append('image')
      .attr('class', 'checkpointIconSvg')
      .attr('width', `${size}px`)
      .attr('height', `${size}px`)
      .attr('href', 'checkpoint.png')
      .attr('opacity', 1.0)
      .attr('x', -halfSize)
      .attr('y', -halfSize)
      .on('mouseover', (event, c) => {
        d3.select(`#checkpointBox-${c.id}`).style('visibility', 'visible')
        d3.select(`#checkpointBoxText-${c.id}`).style('visibility', 'visible')

        d3.select(`#checkpointBoxText-${c.id}`).text((c) => c.deviceName)
        const checkpointTextWidth =
          d3.select(`#checkpointBoxText-${c.id}`).node()?.getBBox()?.width +
          paddingForText
        d3.select(`#checkpointBox-${c.id}`)
          .attr('width', `${checkpointTextWidth}`)
          .attr('x', -checkpointTextWidth / 2)
      })
      .on('mouseout', (event, c) => {
        d3.select(`#checkpointBox-${c.id}`).style('visibility', 'hidden')
        d3.select(`#checkpointBoxText-${c.id}`).style('visibility', 'hidden')
      })
      .on('click', (event, c) => {
        onDeviceClicked(c)
        event.stopPropagation()
      })

    //Checkpoint ToolTip
    g.append('rect')
      .attr('id', (c) => `checkpointBox-${c.id}`)
      .attr('class', 'checkpointToolTipBox')
      .attr('y', tooltipYPosition)
      .attr('rx', `${size * 0.07}px`) // 4px when size is 56
      .attr('height', `${tooltipHeight}px`)
      .style('visibility', 'hidden')

    g.append('text')
      .attr('id', (c) => `checkpointBoxText-${c.id}`)
      .attr('class', 'checkpointToolTipText')
      .attr('dy', tooltipTextYPosition)
      .attr('text-anchor', 'middle')
      .text((c) => c.deviceName)
      .style('visibility', 'hidden')

    // Right ToolTip
    g.append('rect')
      .attr('id', (c) => `moveCircleBox-${c.id}`)
      .attr('class', 'checkpointCircleToolTipR')
      .attr('x', 0)
      .attr('y', tooltipYPosition)
      .attr('rx', `${size * 0.07}px`)
      .attr('width', getEditCheckPointWidth)
      .attr('height', `${tooltipHeight * 0.86}px`)
      .style('visibility', 'hidden')

    g.append('text')
      .attr('id', (c) => `moveCircleText-${c.id}`)
      .attr('class', 'checkpointToolTipR')
      .attr('dx', size * 0.18) // 10px when size is 56
      .attr('dy', tooltipTextYPosition)
      .attr('text-anchor', 'right')
      .text('End Moving')
      .style('visibility', 'hidden')

    // Left ToolTip
    const leftTooltipWidth = size * 3.04 // ~170px when size is 56
    g.append('rect')
      .attr('id', (c) => `removeCircleBox-${c.id}`)
      .attr('class', 'checkpointCircleToolTipL')
      .attr('x', -leftTooltipWidth)
      .attr('y', tooltipYPosition)
      .attr('rx', `${size * 0.07}px`)
      .attr('width', `${leftTooltipWidth}px`)
      .attr('height', `${tooltipHeight * 0.86}px`)
      .style('visibility', 'hidden')

    g.append('text')
      .attr('id', (c) => `removeCircleText-${c.id}`)
      .attr('class', 'checkpointToolTipL')
      .attr('dx', -(leftTooltipWidth - size * 0.18)) // -160 when size is 56
      .attr('dy', tooltipTextYPosition)
      .attr('text-anchor', 'left')
      .text('Remove from map')
      .style('visibility', 'hidden')

    g.append('image')
      .attr('class', 'removeIconSvg')
      .attr('width', `${handleSize}px`)
      .attr('height', `${handleSize}px`)
      .attr('href', 'remove.png')
      .attr('x', leftHandleXPosition)
      .attr('y', handleYPosition)
      .attr('visibility', isHandleVisible)
      .on('mouseover', (event, c) => {
        d3.select(`#removeCircleBox-${c.id}`).style('visibility', 'visible')
        d3.select(`#removeCircleText-${c.id}`).style('visibility', 'visible')
      })
      .on('mouseout', (event, c) => {
        d3.select(`#removeCircleBox-${c.id}`).style('visibility', 'hidden')
        d3.select(`#removeCircleText-${c.id}`).style('visibility', 'hidden')
      })
      .on('click', (event, d) => {
        if (!d.editCheckpointPos) {
          onRemoveCheckpoint(d)
          event.stopPropagation()
        }
      })

    g.append('image')
      .attr('class', 'editIconSvg')
      .attr('width', `${handleSize}px`)
      .attr('height', `${handleSize}px`)
      .attr('href', getEditCheckPointIcon)
      .attr('x', rightHandleXPosition)
      .attr('y', handleYPosition)
      .attr('visibility', isHandleVisible)
      .on('mouseover', (event, c) => {
        d3.select(`#moveCircleBox-${c.id}`).style('visibility', 'visible')
        d3.select(`#moveCircleText-${c.id}`).style('visibility', 'visible')
      })
      .on('mouseout', (event, c) => {
        d3.select(`#moveCircleBox-${c.id}`).style('visibility', 'hidden')
        d3.select(`#moveCircleText-${c.id}`).style('visibility', 'hidden')
      })
      .on('click', (event, d) => {
        onMoveCheckpoint(d)
        event.stopPropagation()
      })
  }

  const checkpointControlUpdate = (update) => {
    update.attr(
      'transform',
      (d) => `translate(${d.projectedX}, ${d.projectedY})`
    )
    update.classed('invalidPosition', (d) => d.invalidPosition)
    update.classed('editOn', (d) => d.isEditMode)
    update.classed('dragged', (d) => d.dragged)

    update.select('.checkpointCircleToolTipL').attr('id', (d) => {
      return `removeCircleBox-${d.id}`
    })
    update.select('.checkpointToolTipL').attr('id', (d) => {
      return `removeCircleText-${d.id}`
    })

    update.select('.checkpointCircleToolTipR').attr('id', (d) => {
      return `moveCircleBox-${d.id}`
    })

    update.select('.checkpointToolTipR').attr('id', (d) => {
      return `moveCircleText-${d.id}`
    })

    update.select('.checkpointToolTipBox').attr('id', (d) => {
      return `checkpointBox-${d.id}`
    })

    update.select('.checkpointToolTipText').attr('id', (d) => {
      return `checkpointBoxText-${d.id}`
    })

    update.select('.removeIconSvg').attr('opacity', getRemoveIconOpacity)
    update.select('.checkpointIconSvg').attr('href', getCheckPointIcon)
    update.select('.removeIconSvg').attr('visibility', isHandleVisible)
    update.select('.editIconSvg').attr('href', getEditCheckPointIcon)
    update.select('.editIconSvg').attr('visibility', isHandleVisible)

    update.select('.checkpointToolTipR').text(getEditCheckPointText)
    update
      .select('.checkpointCircleToolTipR')
      .attr('width', getEditCheckPointWidth)

    update
      .select('.checkpointIconSvg')
      .attr('opacity', (d) =>
        highlightedDeviceIds?.includes(d.id) ? 1.0 : 0.5
      )
  }

  const renderDevices = () => {
    if (!devices) return

    const cameras = devices.filter((d) => d.deviceType === DeviceType.Camera)
    const doors = devices.filter((d) => d.deviceType === DeviceType.Door)
    const alarms = devices.filter((d) => d.deviceType === DeviceType.Alarm)

    // Alarms
    d3.select('.alarm-group')
      .selectAll('g')
      .data(alarms, (d) => d.id)
      .join(
        (enter) => alarmEnter(enter, floorPlanIconSize),
        (update) => alarmUpdate(update, floorPlanIconSize),
        (exit) => exit.remove()
      )

    // Cameras
    d3.select('.camera-group')
      .selectAll('g')
      .data(cameras, (d) => d.id)
      .join(
        (enter) => cameraEnter(enter, floorPlanIconSize * 2),
        (update) => cameraUpdate(update, floorPlanIconSize * 2),
        (exit) => exit.remove()
      )

    // Doors
    d3.select('.door-group')
      .selectAll('g')
      .data(doors, (d) => d.id)
      .join(
        (enter) => doorEnter(enter, floorPlanIconSize),
        (update) => doorUpdate(update, floorPlanIconSize),
        (exit) => exit.remove()
      )

    // When associating devices
    d3.select('.connections-group')
      .selectAll('g')
      .data(connections)
      .join(
        (enter) => connectionEnter(enter, floorPlanIconSize),
        (update) => connectionUpdate(update, floorPlanIconSize),
        (exit) => exit.remove()
      )

    // Controls are smaller than other icons. Make sure user doesn't make them too small
    const controlSize = Math.max(20, floorPlanIconSize)

    d3.select('.camera-tools-group')
      .selectAll('g')
      .data(cameras, (d) => d.id)
      .join(
        (enter) => cameraControlEnter(enter, controlSize),
        (update) => cameraControlUpdate(update, controlSize),
        (exit) => exit.remove()
      )

    // TODO - In future we'll need to scale checkpoints, but it'll require
    //  some additional work to get the pin to go where the location is, not
    //  below
    // For some reason checkpoints require a much larger size, but don't look larger
    // const checkpointScale = 56 / defaultSize

    d3.select('.checkpoint-group')
      .selectAll('g')
      .data(checkpoints)
      .join(
        (enter) => checkpointEnter(enter, 56),
        (update) => checkpointControlUpdate(update),
        (exit) => exit.remove()
      )
  }

  const render = (svgElement, imageElement) => {
    const svg = d3.select(svgElement)
    const { scale } = getMapValues(svgElement, imageElement)
    svg.call(getZoom(scale * 0.5))
  }

  const getMapValues = (svgElement, imageElement) => {
    const img = d3.select(imageElement)
    const imageWidth = Number(img.attr('width'))
    const imageHeight = Number(img.attr('height'))

    const svg = d3.select(svgElement)
    const chartWidth = Number(svg.attr('width'))
    const chartHeight = Number(svg.attr('height'))

    const mapWidth = imageWidth + IMAGE_PADDING
    const mapHeight = imageHeight + IMAGE_PADDING
    const scale = Math.min(chartWidth / mapWidth, chartHeight / mapHeight)
    return { chartWidth, chartHeight, mapWidth, mapHeight, scale }
  }

  const centerZoom = (svgElement, imageElement) => {
    if (!svgElement) return

    const svg = d3.select(svgElement)
    const { chartWidth, chartHeight, mapWidth, mapHeight, scale } =
      getMapValues(svgElement, imageElement)
    let transform = d3.zoomIdentity
    // move to center point
    transform = transform.translate(chartWidth / 2, chartHeight / 2)
    // apply scale
    transform = transform.scale(scale)
    // center content
    transform = transform.translate(
      IMAGE_PADDING / 2 - mapWidth / 2,
      IMAGE_PADDING / 2 - mapHeight / 2
    )
    getZoom(scale).transform(svg, transform)
  }

  useEffect(renderDevices, [devices, checkpoints, floorPlanIconSize])

  return { render, centerZoom }
}
